import { cn } from "../cn";
import { MktButton } from "./primitives";
import { useT } from "../../i18n";
import type { MarketplaceRouteState } from "./mkt-routing";
import type { ZoneKey } from "./mkt-format";

const ZONES: { key: string; labelKey: string }[] = [
  { key: "", labelKey: "marketplace.zones.all" },
  { key: "standard", labelKey: "marketplace.zones.standard" },
  { key: "premium", labelKey: "marketplace.zones.premium" },
  { key: "vip", labelKey: "marketplace.zones.vip" },
  { key: "reserve", labelKey: "marketplace.zones.reserve" },
  { key: "grid_zero", labelKey: "marketplace.zones.gridZero" },
];

const SORTS: { key: string; labelKey: string }[] = [
  { key: "recent", labelKey: "marketplace.land.filters.sortRecent" },
  { key: "price_asc", labelKey: "marketplace.land.filters.sortPriceAsc" },
  { key: "price_desc", labelKey: "marketplace.land.filters.sortPriceDesc" },
  { key: "slots_desc", labelKey: "marketplace.land.filters.sortSlotsDesc" },
];

export function LandFilters({
  route,
  onPatch,
  testId = "land-filters",
}: {
  route: MarketplaceRouteState;
  onPatch: (patch: Record<string, string>) => void;
  testId?: string;
}) {
  const t = useT();
  const q = route.query;
  const activeZone = (q.zone as ZoneKey | undefined) ?? "";
  const hasFilters = !!(q.zone || q.min || q.max || q.minslots || (q.sort && q.sort !== "recent"));

  const inputCls = "w-full h-9 px-2.5 rounded-md bg-elevated/60 border border-border-divider text-text-primary font-mono text-[11px] focus:outline-none focus:border-neon-teal/60 appearance-none";
  const selectCls = "w-full h-9 px-2.5 rounded-md bg-[#0E141B] border border-border-divider text-text-primary font-mono text-[11px] focus:outline-none focus:border-neon-teal/60 appearance-none";
  const labelCls = "font-mono text-[9.5px] tracking-[0.2em] uppercase text-text-muted mb-1.5 block";

  return (
    <div data-testid={testId} className="neon-glass rounded-xl border border-border-divider p-4 space-y-5">
      <div>
        <span className={labelCls}>{t("marketplace.land.filters.zone")}</span>
        <div className="flex flex-col gap-1">
          {ZONES.map((z) => (
            <button
              key={z.key || "all"}
              type="button"
              data-testid={`land-filter-zone-${z.key || "all"}`}
              data-active={activeZone === z.key ? "true" : "false"}
              onClick={() => onPatch({ zone: z.key })}
              className={cn(
                "h-8 px-2.5 rounded-md text-left font-mono text-[10.5px] tracking-[0.1em] uppercase transition-colors duration-fast",
                activeZone === z.key ? "bg-neon-teal/15 text-neon-teal" : "text-text-secondary hover:text-text-primary",
              )}
            >
              {t(z.labelKey)}
            </button>
          ))}
        </div>
      </div>

      <div>
        <span className={labelCls}>{t("marketplace.land.filters.price")}</span>
        <div className="flex items-center gap-2">
          <input
            data-testid="land-filter-min"
            type="number"
            inputMode="decimal"
            placeholder={t("marketplace.land.filters.min")}
            value={q.min ?? ""}
            onChange={(e) => onPatch({ min: e.target.value })}
            className={inputCls}
          />
          <span className="text-text-faint">–</span>
          <input
            data-testid="land-filter-max"
            type="number"
            inputMode="decimal"
            placeholder={t("marketplace.land.filters.max")}
            value={q.max ?? ""}
            onChange={(e) => onPatch({ max: e.target.value })}
            className={inputCls}
          />
        </div>
      </div>

      <div>
        <span className={labelCls}>{t("marketplace.land.filters.minSlots")}</span>
        <input
          data-testid="land-filter-minslots"
          type="number"
          inputMode="numeric"
          placeholder={t("marketplace.land.filters.any")}
          value={q.minslots ?? ""}
          onChange={(e) => onPatch({ minslots: e.target.value })}
          className={inputCls}
        />
      </div>

      <div>
        <span className={labelCls}>{t("marketplace.land.filters.sort")}</span>
        <select
          data-testid="land-filter-sort"
          value={q.sort ?? "recent"}
          onChange={(e) => onPatch({ sort: e.target.value === "recent" ? "" : e.target.value })}
          className={selectCls}
        >
          {SORTS.map((s) => (
            <option key={s.key} value={s.key}>{t(s.labelKey)}</option>
          ))}
        </select>
      </div>

      {hasFilters && (
        <MktButton
          variant="ghost"
          className="w-full"
          testId="land-filter-clear"
          onClick={() => onPatch({ zone: "", min: "", max: "", minslots: "", sort: "" })}
        >
          {t("marketplace.land.filters.clear")}
        </MktButton>
      )}
    </div>
  );
}
